import type { World } from 'koota';
import type { SimEvents } from '../sim/events';
import type { WeaponDef } from '../sim/weapons/schema';
import { group, Layer } from './frame';
import { PRIM_CAPSULE, PRIM_TRIANGLE, withAlpha } from './sdf/primitives';

type Shot = { tick: number; weaponId: string };
const shots = new WeakMap<World, Map<number, Shot>>();
const parries = new WeakMap<World, Map<number, number>>();
const wins = new WeakMap<World, Map<number, number>>();

/** Presentation history never enters the sim, replay tape, or network hash. */
export function observeVisualEvents(world: World, events: SimEvents, tick: number): void {
  let state = shots.get(world);
  if (!state) { state = new Map(); shots.set(world, state); }
  if (events.some((event) => event.type === 'round-phase' && event.phase === 'countdown')) { state.clear(); parries.delete(world); wins.delete(world); }
  for (const event of events) if (event.type === 'block' && event.reflected) {
    let guard = parries.get(world);
    if (!guard) { guard = new Map(); parries.set(world, guard); }
    guard.set(event.player, tick);
  }
  for (const event of events) if (event.type === 'shot') state.set(event.source, { tick, weaponId: event.weaponId });
  for (const event of events) if (event.type === 'kill' && event.source !== event.target) {
    let smiles = wins.get(world);
    if (!smiles) { smiles = new Map(); wins.set(world, smiles); }
    smiles.set(event.source, tick);
  }
}

export function pleasedAfterKO(world: World, player: number, tick: number): boolean {
  const age = tick - (wins.get(world)?.get(player) ?? -Infinity);
  return age >= 0 && age < 65;
}

export function shotAge(world: World, player: number, weaponId: string, tick: number): number {
  const shot = shots.get(world)?.get(player);
  return shot?.weaponId === weaponId ? tick - shot.tick : Infinity;
}

export function parryStrength(world: World, player: number, tick: number): number {
  const age = tick - (parries.get(world)?.get(player) ?? -Infinity);
  return age >= 0 ? Math.max(0, 1 - age / 12) : 0;
}

export function muzzleFlash(def: WeaponDef, x: number, y: number, angle: number, age: number) {
  if (age < 0 || age > 3 || def.category === 'melee' || ['beam', 'field', 'creature', 'return-disc', 'siphon'].includes(def.projectile.kind)) return [];
  const explosive = def.category === 'explosive';
  const length = (explosive ? 0.55 : def.projectile.kind === 'pellets' ? 0.42 : 0.3) * (1 - age * 0.16);
  const color = def.category === 'lava' ? '#ff8d45' : explosive ? '#ffc47d' : '#ffe8a2';
  return [
    group([{ kind: PRIM_TRIANGLE, ax: x, ay: y, bx: length * 0.22, by: length, r: 0.015, rot: angle - Math.PI / 2 }], withAlpha(color, 0.9 - age * 0.12), Layer.Projectiles, { style: 'flat', fx: 'glow', glow: 0.12 }),
    group([{ kind: PRIM_CAPSULE, ax: x, ay: y, bx: x + Math.cos(angle) * length * 0.7, by: y + Math.sin(angle) * length * 0.7, r: 0.035 }], '#fff9e6', Layer.Projectiles, { style: 'flat' }),
  ];
}
